/**
 * Utility functions for input validation and sanitization
 */

/**
 * List of special characters that are not allowed in text inputs
 */
const DISALLOWED_SPECIAL_CHARS = /[{}[\]"'\\:]/g;

/**
 * Sanitizes text input by removing disallowed special characters
 * @param value - The input value to sanitize
 * @returns The sanitized value with special characters removed
 */
export const sanitizeTextInput = (value: string): string => {
  if (typeof value !== 'string') {
    return value;
  }
  return value.replace(DISALLOWED_SPECIAL_CHARS, '');
};

/**
 * Checks if the input contains disallowed special characters
 * @param value - The input value to check
 * @returns true if disallowed characters are found, false otherwise
 */
export const hasDisallowedChars = (value: string): boolean => {
  if (typeof value !== 'string') {
    return false;
  }
  return DISALLOWED_SPECIAL_CHARS.test(value);
};

/**
 * Creates an onChange handler that sanitizes input automatically
 * @param originalOnChange - The original onChange handler
 * @returns A new onChange handler that sanitizes before calling the original
 */
export const createSanitizedOnChange = (
  originalOnChange: (value: string) => void,
) => {
  return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const sanitizedValue = sanitizeTextInput(e.target.value);
    // Only update if value changed (to prevent cursor jumping)
    if (sanitizedValue !== e.target.value) {
      e.target.value = sanitizedValue;
    }
    originalOnChange(sanitizedValue);
  };
};
